"use client";
import { useCallback } from "react";
import { API_BASE_URL } from "@/constants/app.constant";
import { useFetch } from "@/hooks/useFetch";
import { APIResponse } from "@/interfaces/common.interface";
import useAuthService from "./auth.service";
import { PaginationDefaults } from "@/constants/user.constants";
import { UserItem, UserQueryParams } from "@/interfaces/user.interface";
import { GroupInfo } from "@/interfaces/user.interface"; // ⬅ ensure this exists or move to group.interface.ts

/**
 * useUserApiService hook provides user and group management API service functions.
 * Handles user queries, group retrieval, and token reset operations.
 * 
 * @hook
 * @returns {Object} User API service methods
 * @returns {Function} returns.getGroups - Get all user groups
 * @returns {Function} returns.getUsers - Get users with filters, pagination, sorting
 * @returns {Function} returns.resetUserTokens - Reset user tokens for a model
 * 
 * @example
 * ```tsx
 * const { getUsers, getGroups, resetUserTokens } = useUserApiService();
 * 
 * const users = await getUsers({ page: 1, page_size: 10, search: 'John' });
 * const groups = await getGroups();
 * ```
 */
const useUserApiService = () => {
  const fetchService = useFetch();
  const { getUserAccessToken } = useAuthService();

  const USER_API_BASE_URL = `${API_BASE_URL}/admin/users`;
  const GROUPS_API_URL = `${API_BASE_URL}/admin/groups`;

  /**
   * Gets all consolidated user groups.
   * 
   * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
   * @returns {Promise<APIResponse<GroupInfo[]>>} Array of group information
   * @throws {Error} If token is invalid
   */
  const getGroups = useCallback(
    async (signal?: AbortSignal) => {
      const token = await getUserAccessToken();
      if (!token) throw new Error("Invalid token");

      const resp = await fetchService.get<APIResponse<GroupInfo[]>>(GROUPS_API_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return resp;
    },
    [fetchService, getUserAccessToken]
  );

  /**
   * Gets user list with filters, pagination, sorting, and search.
   * Supports query parameters and POST body filters.
   * 
   * @param {UserQueryParams} [params={}] - Query parameters (page, page_size, sortBy, sortOrder, filters, search)
   * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
   * @returns {Promise<APIResponse<UserItem[]>>} Array of user items
   * @throws {Error} If token is invalid
   */
  const getUsers = useCallback(
    async (params: UserQueryParams = {}, signal?: AbortSignal) => {
      const token = await getUserAccessToken();
      if (!token) throw new Error("Invalid token");

      const qs = new URLSearchParams({
        page: String(params.page ?? PaginationDefaults.PAGE),
        size: String(params.page_size ?? PaginationDefaults.PAGE_SIZE),
      });

      if (params.sortBy) qs.append("sortBy", params.sortBy);
      if (params.sortOrder) qs.append("sortOrder", params.sortOrder);

      const url = `${USER_API_BASE_URL}?${qs.toString()}`;

      const body: any = {};

      // apply backend filters
      if (params.filters) {
        Object.assign(body, params.filters);
      }

      if (params.search) {
        body.search = params.search;
        body.name = {
          value: params.search,
          operator: "contains",
        };
      }

      const resp = await fetchService.post<APIResponse<UserItem[]>>(url, {
        body,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        signal,
      });

      return resp;
    },
    [fetchService, getUserAccessToken]
  );

  /**
   * Resets user tokens for a particular model.
   * 
   * @param {number} userId - User ID
   * @param {number} modelId - Model ID to reset tokens for
   * @returns {Promise<APIResponse<any>>} Reset response
   * @throws {Error} If token is invalid
   */
  const resetUserTokens = useCallback(
    async (userId: number, modelId: number) => {
      const token = await getUserAccessToken();
      if (!token) throw new Error("Invalid token");

      const url = `${USER_API_BASE_URL}/${userId}/tokens/reset`;

      const resp = await fetchService.post<APIResponse<any>>(url, {
        body: { model_id: modelId },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      return resp;
    },
    [fetchService, getUserAccessToken]
  );

  return {
    getUsers,
    getGroups,
    resetUserTokens,
  };
};

export default useUserApiService;
